import type { MouseEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { classNames, legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import { useEditorSurface } from '../../EditorSurfaceContext'

// Smart name key — legacy look, rebuilt in flex. Behaviour is component-owned:
// we issue the legacy request and redraw from the answer (busy → ok/wrong),
// instead of depending on legacy toggling classes on the DOM.

const quickRules = [
  { id: 0, label: 'numeric,' },
  { id: 1, label: 'code,' },
  { id: 2, label: 'secret code,' },
  { id: 3, label: 'by topic,' },
  { id: 4, label: 'by content,' },
  { id: 5, label: 'random word' },
]

type SmartStatus = 'idle' | 'busy' | 'ok' | 'wrong'

function runCheck(input: HTMLInputElement, setStatus: (s: SmartStatus) => void) {
  const value = input.value.trim()
  setStatus('busy')
  input.disabled = true
  void dispatchEditorAction({ action: 'check-smart-name-key', value }).then(result => {
    input.disabled = false
    input.value = result.value
    setStatus(result.status)
    input.focus()
  })
}

function runAuto(input: HTMLInputElement, rule: string, setStatus: (s: SmartStatus) => void) {
  setStatus('busy')
  input.disabled = true
  input.value = 'Generating...'
  void dispatchEditorAction({ action: 'generate-smart-name-key', rule }).then(result => {
    input.value = result.value
    input.disabled = false
    input.focus()
    setStatus(result.status)
  })
}

export function SmartNameKey({ maxLength = 85, nameScope = '' }: { maxLength?: number; nameScope?: string }) {
  const { variant, skin } = useEditorSurface()
  const inputRef = useRef<HTMLInputElement>(null)
  const [status, setStatus] = useState<SmartStatus>('idle')
  useEffect(() => setStatus('idle'), [nameScope])
  const isMailbox = variant === 'mailbox'

  // BEHAVIOUR: run the check request for the typed name.
  const handleCheck = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    const input = inputRef.current
    if (input) runCheck(input, setStatus)
  }

  // BEHAVIOUR: pick a quick rule → generate a name.
  const handleQuick = (event: MouseEvent<HTMLElement>, rule: string) => {
    event.preventDefault()
    event.stopPropagation()
    const input = inputRef.current
    if (input) runAuto(input, rule, setStatus)
  }

  // BEHAVIOUR: typing invalidates the previous verdict.
  const handleInput = () => {
    const input = inputRef.current
    if (input) dispatchEditorAction({ action: 'set-smart-name-key', value: input.value })
    setStatus(current => (current === 'idle' ? current : 'idle'))
  }

  const signClass = classNames(
    'md-smart-name__sign',
    skin.smartName.sign,
    status === 'busy' && skin.smartName.signStatus.busy,
    status === 'ok' && skin.smartName.signStatus.ok,
    status === 'wrong' && skin.smartName.signStatus.wrong,
  )
  const inputBoxClass = classNames(
    'md-smart-name__input',
    skin.smartName.inputBox,
    status === 'wrong' && skin.smartName.inputBoxWrong,
  )

  return (
    <div {...legacyHookAttrs('editor.smartName.root', classNames('md-smart-name', skin.smartName.root, `md-smart-name--${status}`))}>
      <div {...legacyHookAttrs('editor.smartName.sign', signClass)}><span>@</span></div>
      <div {...legacyHookAttrs('editor.smartName.inputBox', inputBoxClass)}>
        <input
          ref={inputRef}
          className={skin.smartName.input}
          name="name_key_smart"
          placeholder={isMailbox ? 'Sender name or address' : 'Type a unique name for your msg'}
          type="text"
          maxLength={maxLength}
          autoComplete="off"
          onInput={handleInput}
        />
      </div>
      <div {...legacyHookAttrs('editor.smartName.actions', classNames('md-smart-name__actions', skin.smartName.actions))}>
        <span className={classNames('md-smart-name__check', skin.smartName.check)} {...{ quick_id: '-1' }} role="button" tabIndex={0} onClick={handleCheck}>check</span>
        <div className={classNames('md-smart-name__quick', skin.smartName.quick)}>
          <div className={classNames('md-smart-name__quick-items', skin.smartName.quickItems)}>
            {quickRules.map(rule => (
              <span key={rule.id} className={skin.smartName.quickItem} {...{ quick_id: String(rule.id) }} onClick={event => handleQuick(event, String(rule.id))}>
                {rule.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
